/**
 * The iOS app runs Apple's native Sign in with Apple sheet and hands the
 * result to the hosted page through this window event. The page trades the
 * identity token for a Clerk sign-in ticket at `NATIVE_APPLE_SIGN_IN_PATH`.
 */
export const NATIVE_APPLE_CREDENTIAL_EVENT = "award-party:apple-credential";

export const NATIVE_APPLE_SIGN_IN_PATH = "/api/mobile/apple-sign-in";

/**
 * Apple shares the email and name only on the app's first authorization, and
 * only the user can reset that from iOS Settings. When Apple withholds them,
 * the sign-up stays open here and the page collects them instead.
 */
export const NATIVE_APPLE_SIGN_UP_CONTINUE_PATH = "/api/mobile/apple-sign-in/continue";

export type NativeAppleSignUpNeed = "email" | "name" | "email_code";

const SIGN_UP_NEEDS: ReadonlySet<string> = new Set<NativeAppleSignUpNeed>([
  "email",
  "name",
  "email_code",
]);

const JWT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export type NativeAppleCredential =
  | {
      status: "success";
      identityToken: string;
      firstName: string | null;
      lastName: string | null;
    }
  | { status: "canceled" }
  | { status: "error" };

function optionalName(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function parseNativeAppleCredential(value: unknown): NativeAppleCredential | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const { status, identityToken, firstName, lastName } = value as Record<string, unknown>;
  if (status === "canceled" || status === "error") {
    return { status };
  }
  if (status !== "success" || typeof identityToken !== "string" || !JWT.test(identityToken)) {
    return null;
  }
  return {
    status,
    identityToken,
    firstName: optionalName(firstName),
    lastName: optionalName(lastName),
  };
}

export type NativeAppleTicketResult =
  | { status: "ticket"; ticket: string }
  | { status: "needs"; needs: NativeAppleSignUpNeed[]; pending: string }
  | { status: "failed"; code: string };

export type NativeAppleSignUpStep =
  | { action: "details"; email: string | null; firstName: string | null; lastName: string | null }
  | { action: "verify"; code: string }
  | { action: "resend" };

function parseNeeds(value: unknown): NativeAppleSignUpNeed[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  return value.every((need) => typeof need === "string" && SIGN_UP_NEEDS.has(need))
    ? (value as NativeAppleSignUpNeed[])
    : null;
}

async function postForTicket(
  fetchImpl: typeof fetch,
  path: string,
  body: unknown,
): Promise<NativeAppleTicketResult> {
  let response: Response;
  try {
    response = await fetchImpl(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
      cache: "no-store",
    });
  } catch {
    return { status: "failed", code: "network" };
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // Non-JSON bodies (proxy error pages) fall through to the status code.
  }
  const { ticket, needs, pending, error } = (
    payload && typeof payload === "object" ? payload : {}
  ) as Record<string, unknown>;
  if (response.ok && typeof ticket === "string" && ticket.length > 0) {
    return { status: "ticket", ticket };
  }
  const parsedNeeds = parseNeeds(needs);
  if (response.ok && parsedNeeds && typeof pending === "string" && pending.length > 0) {
    return { status: "needs", needs: parsedNeeds, pending };
  }
  return {
    status: "failed",
    code: typeof error === "string" && error.length > 0 ? error : `http_${response.status}`,
  };
}

export function requestNativeAppleSignInTicket(
  fetchImpl: typeof fetch,
  credential: Extract<NativeAppleCredential, { status: "success" }>,
): Promise<NativeAppleTicketResult> {
  return postForTicket(fetchImpl, NATIVE_APPLE_SIGN_IN_PATH, credential);
}

export function continueNativeAppleSignUp(
  fetchImpl: typeof fetch,
  pending: string,
  step: NativeAppleSignUpStep,
): Promise<NativeAppleTicketResult> {
  return postForTicket(fetchImpl, NATIVE_APPLE_SIGN_UP_CONTINUE_PATH, { ...step, pending });
}
